"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { GiPill } from "react-icons/gi";
import { HiOutlinePlus } from "react-icons/hi";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue, useSearchSelect } from "@/components/ui/select/search-select";
import Textarea from "@/components/ui/textarea";
import { useModule } from "@/hooks/use-module";
import type { PatientSupplement, PatientSupplementFields } from "@/types/patient-supplement";
import { useArchivePatientSupplementMutation, useCreatePatientSupplementMutation, usePatientSupplementsQuery, useSupplementNamesQuery, useUpdatePatientSupplementMutation } from "@/query/patient/supplements";
interface SupplementsSectionProps {
  patientId: string;
  id?: string;
  persistKey: string;
}
export default function SupplementsSection(t0) {
  const $ = _c(22);
  const {
    patientId,
    id,
    persistKey
  } = t0;
  const {
    data: supplements,
    isLoading
  } = usePatientSupplementsQuery(patientId);
  const archiveMutation = useArchivePatientSupplementMutation(patientId);
  const [removing, setRemoving] = useState(null);
  const [editing, setEditing] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Button variant="secondary" size="sm" icon={FiPlus} onClick={() => setEditing("new")}>Add Supplement</Button>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isLoading || $[2] !== supplements) {
    t2 = isLoading ? <div className="space-y-2">{[...Array(2)].map(_temp)}</div> : supplements && supplements.length > 0 ? <div className="overflow-x-auto rounded-md border"><table className="w-full text-left"><thead className="bg-background-secondary text-overline text-muted-foreground"><tr><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Dose</th><th className="px-4 py-3 font-medium">Frequency</th><th className="px-4 py-3 font-medium">Instructions</th><th className="px-4 py-3 font-medium">Location</th><th className="w-20 px-4 py-3" /></tr></thead><tbody className="divide-y divide-border text-body">{supplements.map(supplement => <tr key={supplement.id} className="align-top"><td className="px-4 py-3"><div className="flex flex-wrap items-center gap-2"><span className="text-body-strong text-foreground">{supplement.name}</span>{supplement.source === "FULLSCRIPT" && <Badge size="xs" variant="success">Fullscript</Badge>}</div>{supplement.brand && <p className="text-caption text-foreground-secondary">{supplement.brand}</p>}</td><td className="px-4 py-3 text-foreground-secondary">{supplement.dose || "\u2014"}</td><td className="px-4 py-3 text-foreground-secondary">{supplement.frequency || "\u2014"}</td><td className="whitespace-pre-wrap px-4 py-3 text-foreground-secondary">{supplement.instructions || "\u2014"}</td><td className="px-4 py-3 text-foreground-secondary">{supplement.location || "\u2014"}</td><td className="px-4 py-3">{supplement.source === "MANUAL" && <div className="flex justify-end gap-1"><Button variant="ghost" size="sm" icon={FiEdit2} aria-label={`Edit ${supplement.name}`} onClick={() => setEditing(supplement)} /><Button variant="ghost" size="sm" icon={FiTrash2} aria-label={`Remove ${supplement.name}`} onClick={() => setRemoving(supplement)} /></div>}</td></tr>)}</tbody></table></div> : <div className="py-8 text-center"><GiPill className="mx-auto h-8 w-8 text-foreground-muted" /><p className="mt-2 text-body text-muted-foreground">No supplements recorded</p></div>;
    $[1] = isLoading;
    $[2] = supplements;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = removing !== null;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = open => !open && setRemoving(null);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== archiveMutation || $[6] !== removing) {
    t5 = async () => {
      if (!removing) {
        return;
      }
      try {
        await archiveMutation.mutateAsync(removing.id);
        setRemoving(null);
      } catch {}
    };
    $[5] = archiveMutation;
    $[6] = removing;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `Remove ${removing?.name ?? "this supplement"} from the patient's chart?`;
  let t7;
  if ($[8] !== archiveMutation.isPending || $[9] !== t3 || $[10] !== t5 || $[11] !== t6) {
    t7 = <DeleteConfirmationModal isOpen={t3} setIsOpen={t4} onConfirm={t5} isLoading={archiveMutation.isPending} title="Remove supplement" subtitle={t6} confirmText="Remove" />;
    $[8] = archiveMutation.isPending;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== editing || $[14] !== patientId) {
    t8 = editing && <SupplementDialog patientId={patientId} supplement={editing === "new" ? null : editing} onClose={() => setEditing(null)} />;
    $[13] = editing;
    $[14] = patientId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== id || $[17] !== persistKey || $[18] !== t2 || $[19] !== t7 || $[20] !== t8) {
    t9 = <CollapsibleSection title="Supplements" id={id} persistKey={persistKey} actions={t1}>{t2}{t7}{t8}</CollapsibleSection>;
    $[16] = id;
    $[17] = persistKey;
    $[18] = t2;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp(_, i) {
  return <div key={i} className="h-12 w-full animate-pulse rounded bg-muted" />;
}
interface SupplementDialogProps {
  patientId: string;
  supplement: PatientSupplement | null;
  onClose: () => void;
}
function SupplementDialog(t0) {
  const $ = _c(77);
  const {
    patientId,
    supplement,
    onClose
  } = t0;
  const t1 = supplement?.name ?? "";
  const t2 = supplement?.dose ?? "";
  const t3 = supplement?.frequency ?? "";
  const t4 = supplement?.instructions ?? "";
  const t5 = supplement?.location ?? "";
  let t6;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      name: t1,
      dose: t2,
      frequency: t3,
      instructions: t4,
      location: t5
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [fields, setFields] = useState(t6);
  const [showNameError, setShowNameError] = useState(false);
  const createMutation = useCreatePatientSupplementMutation(patientId);
  const updateMutation = useUpdatePatientSupplementMutation(patientId);
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const hasFullscriptSupplements = useModule("FULLSCRIPT_SUPPLEMENTS");
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (key, value) => setFields(prev => ({
      ...prev,
      [key]: value
    }));
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const setField = t7;
  let t8;
  if ($[7] !== createMutation || $[8] !== fields.dose || $[9] !== fields.frequency || $[10] !== fields.instructions || $[11] !== fields.location || $[12] !== fields.name || $[13] !== onClose || $[14] !== supplement || $[15] !== updateMutation) {
    t8 = async () => {
      const name = fields.name.trim();
      if (!name) {
        setShowNameError(true);
        return;
      }
      const payload = {
        name,
        dose: fields.dose?.trim() || null,
        frequency: fields.frequency?.trim() || null,
        instructions: fields.instructions?.trim() || null,
        location: fields.location?.trim() || null
      };
      try {
        if (supplement) {
          await updateMutation.mutateAsync({
            id: supplement.id,
            fields: payload
          });
        } else {
          await createMutation.mutateAsync(payload);
        }
        onClose();
      } catch {}
    };
    $[7] = createMutation;
    $[8] = fields.dose;
    $[9] = fields.frequency;
    $[10] = fields.instructions;
    $[11] = fields.location;
    $[12] = fields.name;
    $[13] = onClose;
    $[14] = supplement;
    $[15] = updateMutation;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleSave = t8;
  let t9;
  if ($[17] !== onClose) {
    t9 = open => !open && onClose();
    $[17] = onClose;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = supplement ? "Edit supplement" : "Add supplement";
  let t11;
  if ($[19] !== t10) {
    t11 = <DialogTitle>{t10}</DialogTitle>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = hasFullscriptSupplements ? "Record a supplement the patient takes outside of Fullscript." : "Record a supplement the patient takes.";
  let t13;
  if ($[21] !== t12) {
    t13 = <DialogDescription className="mt-1 text-body leading-6">{t12}</DialogDescription>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t13) {
    t14 = <DialogHeader>{t11}{t13}</DialogHeader>;
    $[23] = t11;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Label>Name</Label>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = name_0 => {
      setField("name", name_0);
      setShowNameError(false);
    };
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== fields.name || $[29] !== showNameError) {
    t17 = <SupplementNameSelect value={fields.name} onChange={t16} hasError={showNameError} />;
    $[28] = fields.name;
    $[29] = showNameError;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== showNameError) {
    t18 = showNameError && <p className="text-caption text-status-error-text">Name is required</p>;
    $[31] = showNameError;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <div className="space-y-1.5">{t15}{t17}{t18}</div>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Label htmlFor="supplement-dose">Dose</Label>;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = fields.dose ?? "";
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = e => setField("dose", e.target.value);
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t21) {
    t23 = <div className="space-y-1.5">{t20}<Input id="supplement-dose" placeholder="e.g. 1000 mg" value={t21} onChange={t22} /></div>;
    $[38] = t21;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Label htmlFor="supplement-frequency">Frequency</Label>;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const t25 = fields.frequency ?? "";
  let t26;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e_0 => setField("frequency", e_0.target.value);
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== t25) {
    t27 = <div className="space-y-1.5">{t24}<Input id="supplement-frequency" placeholder="e.g. Twice daily" value={t25} onChange={t26} /></div>;
    $[42] = t25;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t23 || $[45] !== t27) {
    t28 = <div className="grid gap-4 sm:grid-cols-2">{t23}{t27}</div>;
    $[44] = t23;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Label htmlFor="supplement-instructions">Administration instructions</Label>;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  const t30 = fields.instructions ?? "";
  let t31;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_1 => setField("instructions", e_1.target.value);
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t30) {
    t32 = <div className="space-y-1.5">{t29}<Textarea id="supplement-instructions" placeholder="e.g. Take with food" value={t30} onChange={t31} /></div>;
    $[49] = t30;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Label htmlFor="supplement-location">Location</Label>;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  const t34 = fields.location ?? "";
  let t35;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = e_2 => setField("location", e_2.target.value);
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] !== t34) {
    t36 = <div className="space-y-1.5">{t33}<Input id="supplement-location" placeholder="Where the patient gets it, e.g. Whole Foods" value={t34} onChange={t35} /></div>;
    $[53] = t34;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  let t37;
  if ($[55] !== t19 || $[56] !== t28 || $[57] !== t32 || $[58] !== t36) {
    t37 = <DialogBody className="space-y-4">{t19}{t28}{t32}{t36}</DialogBody>;
    $[55] = t19;
    $[56] = t28;
    $[57] = t32;
    $[58] = t36;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  let t38;
  if ($[60] !== isSaving || $[61] !== onClose) {
    t38 = <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>;
    $[60] = isSaving;
    $[61] = onClose;
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  const t39 = supplement ? "Save" : "Add supplement";
  let t40;
  if ($[63] !== handleSave || $[64] !== isSaving || $[65] !== t39) {
    t40 = <Button onClick={handleSave} isLoading={isSaving} loadingText="Saving...">{t39}</Button>;
    $[63] = handleSave;
    $[64] = isSaving;
    $[65] = t39;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t38 || $[68] !== t40) {
    t41 = <DialogFooter>{t38}{t40}</DialogFooter>;
    $[67] = t38;
    $[68] = t40;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t14 || $[71] !== t37 || $[72] !== t41) {
    t42 = <DialogContent className="sm:max-w-lg">{t14}{t37}{t41}</DialogContent>;
    $[70] = t14;
    $[71] = t37;
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] !== t42 || $[75] !== t9) {
    t43 = <Dialog open={true} onOpenChange={t9}>{t42}</Dialog>;
    $[74] = t42;
    $[75] = t9;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  return t43;
}
interface SupplementNameSelectProps {
  value: string;
  onChange: (name: string) => void;
  hasError?: boolean;
}
function SupplementNameSelect(t0) {
  const $ = _c(24);
  const {
    value,
    onChange,
    hasError
  } = t0;
  const [searchTerm, setSearchTerm] = useState("");
  const {
    data: names
  } = useSupplementNamesQuery(true);
  let options;
  let t1;
  let typed;
  if ($[0] !== names || $[1] !== searchTerm || $[2] !== value) {
    options = value && !(names ?? []).some(n => n.toLowerCase() === value.toLowerCase()) ? [value, ...(names ?? [])] : names ?? [];
    typed = searchTerm.trim();
    t1 = typed.length > 0 && !options.some(name => name.toLowerCase() === typed.toLowerCase());
    $[0] = names;
    $[1] = searchTerm;
    $[2] = value;
    $[3] = options;
    $[4] = t1;
    $[5] = typed;
  } else {
    options = $[3];
    t1 = $[4];
    typed = $[5];
  }
  const canAddTyped = t1;
  let t2;
  if ($[6] !== onChange) {
    t2 = name_0 => {
      if (name_0) {
        onChange(name_0);
      }
    };
    $[6] = onChange;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SearchSelectValue placeholder="Search or add a supplement" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== hasError) {
    t4 = <SearchSelectTrigger hasError={hasError}>{t3}</SearchSelectTrigger>;
    $[9] = hasError;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== canAddTyped || $[12] !== typed) {
    t5 = canAddTyped ? <AddTypedName name={typed} /> : undefined;
    $[11] = canAddTyped;
    $[12] = typed;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== options) {
    t6 = options.map(_temp2);
    $[14] = options;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <SearchSelectContent searchPlaceholder="Search supplements..." emptyMessage="No matching supplements" footerAction={t5}>{t6}</SearchSelectContent>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t2 || $[20] !== t4 || $[21] !== t7 || $[22] !== value) {
    t8 = <SearchSelect value={value} onValueChange={t2} onSearchTermChange={setSearchTerm}>{t4}{t7}</SearchSelect>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t7;
    $[22] = value;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp2(name_1) {
  return <SearchSelectItem key={name_1} value={name_1}>{name_1}</SearchSelectItem>;
}
function AddTypedName(t0) {
  const $ = _c(7);
  const {
    name
  } = t0;
  const {
    onValueChange
  } = useSearchSelect();
  let t1;
  if ($[0] !== name || $[1] !== onValueChange) {
    t1 = () => onValueChange(name);
    $[0] = name;
    $[1] = onValueChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HiOutlinePlus className="h-4 w-4" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== name || $[5] !== t1) {
    t3 = <button type="button" onClick={t1} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-body text-foreground-secondary hover:bg-hover">{t2}Add "{name}"</button>;
    $[4] = name;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}